/* ThisThat V4.9.6 — global top-layer search + hierarchy-code resolver. */
(() => {
  if (window.__TT_SEARCH_V2__) return;
  window.__TT_SEARCH_V2__ = true;

  const dialog = document.querySelector('[data-ttsearch-dialog]');
  if (!dialog) return;

  const input = dialog.querySelector('.ttsearch-input');
  const empty = dialog.querySelector('[data-ttsearch-empty]');
  const closeButton = dialog.querySelector('[data-ttsearch-close]');
  const getTriggers = () => Array.from(document.querySelectorAll('[data-ttsearch-trigger]'));
  let lastTrigger = null;

  const normalizeCode = (value) => String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  const escapeHTML = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // V4.9.5 deliberately uses one JSON node per product. That means a problem
  // with one record cannot invalidate the entire catalogue index, and we can
  // merge a current-product/fallback record safely.
  const codeIndex = [];
  const seenEntries = new Set();
  document.querySelectorAll('[data-tt-code-product]').forEach((node) => {
    try {
      const product = JSON.parse(node.textContent || '{}');
      const variants = Array.isArray(product.variants) && product.variants.length
        ? product.variants
        : [{ variantId: null, size: '', sizeCode: '', itemCode: product.productCode, variantUrl: product.productUrl, price: '', available: true }];

      variants.forEach((variant) => {
        const entry = {
          productId: product.productId,
          variantId: variant.variantId,
          title: product.title,
          subtitle: product.subtitle,
          colour: product.colour,
          rangeCode: product.rangeCode,
          styleCode: product.styleCode,
          garmentCode: product.garmentCode,
          colourCode: product.colourCode,
          sizeCode: variant.sizeCode,
          size: variant.size,
          productCode: product.productCode,
          itemCode: variant.itemCode || product.productCode,
          productUrl: product.productUrl,
          variantUrl: variant.variantUrl || product.productUrl,
          image: product.image,
          price: variant.price,
          available: variant.available,
        };

        const key = `${entry.productId || entry.productUrl}:${entry.variantId || normalizeCode(entry.itemCode)}`;
        if (seenEntries.has(key)) return;
        seenEntries.add(key);
        codeIndex.push(entry);
      });
    } catch (error) {
      console.warn('ThisThat skipped an invalid catalogue-code product entry.', error);
    }
  });

  if (!codeIndex.length) {
    console.warn('ThisThat catalogue-code index is empty. Check Product Style storefront access and product references.');
  }

  const codeMatches = (rawQuery) => {
    const query = normalizeCode(rawQuery);
    if (query.length < 2 || !codeIndex.length) return [];

    return codeIndex.filter((entry) => {
      const productCode = normalizeCode(entry.productCode);
      const itemCode = normalizeCode(entry.itemCode);
      const segments = [entry.rangeCode, entry.styleCode, entry.garmentCode, entry.colourCode, entry.sizeCode]
        .map(normalizeCode)
        .filter(Boolean);

      return productCode.startsWith(query)
        || itemCode.startsWith(query)
        || productCode.includes(query)
        || itemCode.includes(query)
        || segments.some((segment) => segment === query || segment.startsWith(query));
    });
  };

  const getCodeResultData = (rawQuery) => {
    const query = normalizeCode(rawQuery);
    const matches = codeMatches(rawQuery);
    if (!matches.length) return null;

    const exactItems = matches.filter((entry) => normalizeCode(entry.itemCode) === query);
    if (exactItems.length) {
      return { entries: exactItems.slice(0, 6), variantLevel: true, exact: true };
    }

    const exactProducts = matches.filter((entry) => normalizeCode(entry.productCode) === query);
    const source = exactProducts.length ? exactProducts : matches;
    const seen = new Set();
    const grouped = [];
    source.forEach((entry) => {
      const key = String(entry.productId || entry.productUrl || entry.productCode);
      if (seen.has(key)) return;
      seen.add(key);
      grouped.push(entry);
    });

    return { entries: grouped.slice(0, 6), variantLevel: false, exact: exactProducts.length > 0 };
  };

  const renderCodeMarkup = (rawQuery) => {
    const result = getCodeResultData(rawQuery);
    if (!result) return null;

    const items = result.entries.map((entry, index) => {
      const href = result.variantLevel ? entry.variantUrl : entry.productUrl;
      const code = result.variantLevel ? entry.itemCode : entry.productCode;
      const details = [entry.subtitle, entry.colour, result.variantLevel ? entry.size : null]
        .filter(Boolean)
        .map(escapeHTML)
        .join(' · ');
      const image = entry.image
        ? `<span class="ttsearch-code-result__image"><img src="${escapeHTML(entry.image)}" alt="" loading="lazy" width="72" height="72"></span>`
        : '<span class="ttsearch-code-result__image ttsearch-code-result__image--empty" aria-hidden="true"></span>';

      return `<li id="predictive-search-option-code-${index + 1}" role="option" aria-selected="false">
        <a class="ttsearch-code-result" href="${escapeHTML(href)}" tabindex="-1"${index === 0 ? ' data-tt-code-result-primary' : ''}>
          ${image}
          <span class="ttsearch-code-result__copy">
            <strong>${escapeHTML(entry.title)}</strong>
            ${details ? `<span>${details}</span>` : ''}
            <code>${escapeHTML(code)}</code>
          </span>
          <span class="ttsearch-code-result__price">${escapeHTML(entry.price)}</span>
        </a>
      </li>`;
    }).join('');

    const count = result.entries.length;
    return `<div id="predictive-search-results" class="ttsearch-results ttsearch-results--codes" role="listbox">
      <section class="ttsearch-results__group" aria-labelledby="TTSearchCodes">
        <h2 id="TTSearchCodes" class="ttsearch-results__label">Catalogue codes</h2>
        <ul class="ttsearch-code-results list-unstyled" role="group" aria-labelledby="TTSearchCodes">${items}</ul>
      </section>
      <div class="ttsearch-code-hint">Dashes and spaces are optional — <strong>${escapeHTML(rawQuery)}</strong> is matched as <strong>${escapeHTML(normalizeCode(rawQuery))}</strong>.</div>
      <span class="hidden" data-predictive-search-live-region-count-value>${count} catalogue ${count === 1 ? 'result' : 'results'} for ${escapeHTML(rawQuery)}</span>
    </div>`;
  };

  window.TTCodeSearch = {
    normalize: normalizeCode,
    getMatches: codeMatches,
    getMarkup: renderCodeMarkup,
    getPrimaryUrl(rawQuery) {
      const result = getCodeResultData(rawQuery);
      if (!result?.entries?.length) return null;
      const first = result.entries[0];
      return result.variantLevel ? first.variantUrl : first.productUrl;
    },
    debug() {
      return { count: codeIndex.length, entries: codeIndex };
    },
  };

  const syncSearchState = () => {
    if (!input || !empty) return;
    empty.hidden = input.value.trim().length > 0;
  };

  const syncOpenState = () => {
    const open = dialog.open;
    document.documentElement.classList.toggle('ttsearch-open', open);
    document.body.classList.toggle('overflow-hidden', open);
    getTriggers().forEach((trigger) => trigger.setAttribute('aria-expanded', open ? 'true' : 'false'));
    if (open) syncSearchState();
  };

  const openSearch = (trigger) => {
    lastTrigger = trigger || document.activeElement;
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    syncOpenState();
    requestAnimationFrame(() => input?.focus({ preventScroll: true }));
  };

  const closeSearch = () => {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    syncOpenState();
    if (lastTrigger?.focus) requestAnimationFrame(() => lastTrigger.focus({ preventScroll: true }));
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-ttsearch-trigger]');
    if (trigger) {
      event.preventDefault();
      openSearch(trigger);
      return;
    }

    if (event.target.closest?.('[data-ttsearch-close]')) {
      event.preventDefault();
      closeSearch();
    }
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeSearch();
  });

  dialog.addEventListener('close', syncOpenState);
  dialog.addEventListener('cancel', () => requestAnimationFrame(syncOpenState));

  dialog.addEventListener('input', (event) => {
    if (!event.target.closest('.ttsearch-input')) return;
    syncSearchState();
  });

  dialog.addEventListener('reset', (event) => {
    if (!event.target.closest('.ttsearch-form')) return;
    requestAnimationFrame(syncSearchState);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const active = document.activeElement;
    if (active && /INPUT|TEXTAREA|SELECT/.test(active.tagName)) return;
    if (dialog.open) return;
    const trigger = document.querySelector('[data-ttsearch-trigger]');
    if (!trigger) return;
    event.preventDefault();
    openSearch(trigger);
  });

  window.addEventListener('pageshow', syncOpenState);
  syncSearchState();
  syncOpenState();
})();
